import { Button, Chip, Container, Grid2, Popover, TextField, Typography } from "@mui/material";
import { get } from "lodash";
import React, { useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import AddAdvertisingForm from "components/advertising/AddAdvertisingForm";
import ContactDropdown from "components/common/Contacts";
import ContactList from "components/common/Contacts/ContactList";
import StatusHandler from "components/common/StatusHandler";
import { SMALL_DEVICE_MEDIA_SIZE } from "components/common/constants";
import SimpleContact from "components/contact/SimpleContact";
import { PdfViewer } from "components/fileViewers/container";
import EmailDialog from "components/report/emailDialog";
import { ACTION_STATES } from "core/actions";
import { assertArray, assertObject } from "core/validation";
import log from "loglevel";
import PropTypes from "prop-types";
import { ReportAdvTable, getReportTypesStrByAdvTypes } from "./container";
import { responsiveStyle } from "./styles";
import AtsDatePicker from "components/common/form/AtsDatePicker";
import {formatDate} from "components/common/dateUtils";

function AdvReport({
    filmRelease,
    onLoadReportView,
    onClear,
    onPreviewPdf,
    loadingStatus,
    ccList,
    advertisementTypes,
    onSetPrimaryUser,
    onSetCCList,
    onSendEmail,
    sendingEmail,
    onResetEmailState,
    saveContact,
    getCompanyAndContacts,
    contactState,
    primaryUser,
}) {
    const classes = responsiveStyle();
    const [reportDate, setReportDate] = useState(new Date());

    const [comment, setComment] = useState(null);
    const [openEmailDialog, setOpenEmailDialog] = useState(false);
    const [customContact, setCustomContact] = useState(null);
    const [contactAnchor, setContactAnchor] = useState(null);
    const [openCCSimpleContact, setOpenCCSimpleContact] = useState(false);
    const [newSavedContacts, setNewSavedContacts] = useState([]);
    const title = get(filmRelease, "title", "Please select a film");
    const companyName = get(filmRelease, "companyId.title", "");
    const companyId = get(filmRelease, "companyId.id", null);
    const _contacts = get(filmRelease, "contacts", []);
    const releaseType = get(filmRelease, "filmReleaseTypeId.name", "");
    const rating = get(filmRelease, "filmRatingTypeId.name", null);
    const theme = useTheme();
    const isSmallDevice = useMediaQuery(theme.breakpoints.down(SMALL_DEVICE_MEDIA_SIZE));

    useEffect(() => {
        if (sendingEmail === ACTION_STATES.SUCCESS && openEmailDialog) {
            setOpenEmailDialog(false);
            onResetEmailState();
        }
    }, [onResetEmailState, openEmailDialog, sendingEmail]);

    useEffect(() => {
        if (openCCSimpleContact) {
            let _newSavedContacts = newSavedContacts;
            _newSavedContacts.push(contactState.selectedContact);
            setNewSavedContacts(_newSavedContacts);
            const newContact = [contactState.selectedContact];
            const _ccList = ccList.concat(newContact);
            onSetCCList(_ccList);
            setOpenCCSimpleContact(false);
        }
    }, [
        ccList,
        contactState.selectedContact,
        contactState.selectedContact.id,
        newSavedContacts,
        onSetCCList,
        openCCSimpleContact,
    ]);

    useEffect(() => {
        log.debug(filmRelease);
        if (filmRelease) {
            onLoadReportView();
        }
    }, []);

    function handleReportDateChange(date) {
        setReportDate(date);
    }
    function handleViewReport() {
        const date =formatDate(reportDate);
        const id = primaryUser.id || null;
        const name = primaryUser.name;
        onPreviewPdf(date, id, name, comment);
    }

    function handleOnChangeComment(e) {
        setComment(e.target.value);
    }

    function handleOnClear() {
        onClear();
    }

    function handleOnSend() {
        setOpenEmailDialog(true);
    }

    function handleReportSend() {
        setOpenEmailDialog(true);
    }
    function handleEmailDialogOnClose() {
        setOpenEmailDialog(false);
    }

    function handleOnSendEmail(message) {
        const date = formatDate(reportDate);
        const id = primaryUser.id || null;
        const name = primaryUser.name;
        onSendEmail(date, id, name, comment, message);
    }

    function handlePrimaryUserChange(arrayUser) {
        onSetPrimaryUser(assertArray(arrayUser) ? arrayUser[0] : null);
    }

    function handleOnAddContactsToCc(_contacts) {
        onSetCCList(_contacts);
    }
    function onRefreshForm() {
        if (filmRelease) {
            onLoadReportView(true);
        }
    }

    function handleAddSimpleContact(contact) {
        setContactAnchor(null);
        setCustomContact(contact);
        if (openCCSimpleContact) {
            const _contact = {
                id: Date.now() * -1,
                addresses: [{ email: contact.email }],
                firstName: contact.name,
                lastName: contact.lastName,
            };
            let _newSavedContacts = newSavedContacts;
            _newSavedContacts.push(_contact);
            setNewSavedContacts(_newSavedContacts);
            const _ccList = ccList.concat([_contact]);
            onSetCCList(_ccList);
            setOpenCCSimpleContact(false);
        } else onSetPrimaryUser(contact);
    }

    function handleSaveSimpleContact(contact) {
        const newContact = {
            companyId: companyId,
            advertisementTypesIds: [],
            contactRolesIds: [],
            firstName: contact.name,
            lastName: contact.lastName,
            title: "",
            addresses: [{ email: contact.email }],
            assistant: "",
            assistantPhone: "",
            note: "",
        };

        saveContact(newContact);
        getCompanyAndContacts(companyId);
        setContactAnchor(null);
        setCustomContact(newContact);
        if (!openCCSimpleContact) {
            const user = {
                name: `${contact.name} ${contact.lastName}`,
                email: contact.email,
            };
            onSetPrimaryUser(user);
        }
    }

    function handleCloseSimpleContact() {
        setContactAnchor(null);
        setCustomContact(null);
    }

    function handleButtonAddSimpleContact(event) {
        setOpenCCSimpleContact(false);
        setContactAnchor(event.currentTarget);
        setCustomContact({});
    }

    function handleButtonAddSimpleContactForCC(event) {
        setContactAnchor(event.currentTarget);
        setCustomContact({});
        setOpenCCSimpleContact(true);
    }

    const previewTitle = title || "Report";

    const contacts = useMemo(() => {
        return _contacts;
    }, [_contacts]);

    const subject = useMemo(() => {
        if (openEmailDialog && filmRelease) {
            const adType = assertArray(advertisementTypes) ? getReportTypesStrByAdvTypes(advertisementTypes) : "";
            return `${filmRelease.title} ${adType} Advertising Report`;
        }
    }, [advertisementTypes, filmRelease, openEmailDialog]);
    const contactsLength = assertArray(contacts) ? contacts.length : 0;

    const primaryContacts = useMemo(() => {
        const result = contacts;
        return result;
    }, [contacts]);

    const ccContacts = useMemo(() => {
        if (newSavedContacts.length === 0) {
            const _ccList = ccList.filter((c) => c.id > 0);
            onSetCCList(_ccList);
        }
        const result = newSavedContacts.length > 0 ? contacts.concat(newSavedContacts) : contacts;
        log.debug(result);
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactsLength, newSavedContacts.length]);

    const _primaryValue = primaryUser ? [primaryUser] : [];

    const ratingAndRelease = (
        <div>
            <span style={{ opacity: 0.7 }}>{releaseType}</span>
            {rating && <Chip style={{ marginLeft: 10 }} variant="outlined" size="small" label={rating} />}
        </div>
    );

    const disableBtns = !assertObject(primaryUser);
    const sendDateAndBtn = (
        <Grid2 container  direction="column">
            <Typography variant="subtitle2">Report Date</Typography>
            <AtsDatePicker value={reportDate} onChange={handleReportDateChange}/>
            {}
            {}
            {}
            {}
            {}
            {}
            {}
            {}
            {}
            {}
            {}
            {}
            {}
        </Grid2>
    );

    const leftSide = (
        <Grid2
            container
            spacing={2}
            style={{ marginBottom: 15 }}
            size={{
                md: 4,
                xs: 12
            }}>
            <Grid2 container spacing={2} size={12}>
                <Grid2 size={12}>
                    <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="subtitle2">Primary recipient</Typography>
                        <IconButton disabled={!companyId} onClick={handleButtonAddSimpleContact}>
                            <AddIcon />
                        </IconButton>
                    </Box>

                    <ContactDropdown
                        isSingle
                        readOnly={false}
                        hideContactsPreview
                        showEmailFirst
                        value={_primaryValue}
                        onChange={handlePrimaryUserChange}
                        options={primaryContacts}
                    />
                </Grid2>
                <Grid2 size={12}>
                    <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="subtitle2">CC recipients</Typography>
                        <IconButton disabled={!companyId} onClick={handleButtonAddSimpleContactForCC}>
                            <AddIcon />
                        </IconButton>
                    </Box>
                    <ContactList
                        listStyle={{ height: 180 }}
                        selectedContacts={ccList}
                        onSelectionChange={handleOnAddContactsToCc}
                        contacts={ccContacts}
                    />
                </Grid2>
            </Grid2>
            <Grid2 container size={12}>
                <Grid2 size={12}>
                    <TextField
                        className="form-control"
                        margin="normal"
                        label="Report Comment"
                        variant="outlined"
                        multiline={true}
                        value={comment}
                        onChange={handleOnChangeComment}
                        rows={isSmallDevice ? 3 : 7}
                        rowsMax="20"
                    />
                </Grid2>
            </Grid2>
            {sendDateAndBtn}
        </Grid2>
    );

    const rightSide = (
        <Grid2
            container
            direction="column"
            size={{
                md: 8,
                xs: 12
            }}>
            <Grid2 container  direction="row" style={{ marginBottom: 5 }}>
                <Grid2 container direction="column"  className={classes.titleContainer} size={9}>
                    <Typography variant="button">{title}</Typography>
                    {ratingAndRelease}
                    <Typography variant="subtitle2" style={{ opacity: 0.7 }} display="block">
                        {companyName}
                    </Typography>
                </Grid2>
                <Grid2 container direction="column" size={3}>
                    <Button
                        size="small"
                        variant={"contained"}
                        style={{ marginTop: 10 }}
                        onClick={handleViewReport}
                        disabled={disableBtns}
                        color={"secondary"}
                    >
                        Preview Report
                    </Button>

                    <Button
                        size="small"
                        variant={"outlined"}
                        style={{ marginTop: 10 }}
                        onClick={handleOnSend}
                        disabled={disableBtns}
                        color={"secondary"}
                    >
                        Send Report
                    </Button>

                    <Button size="small" variant={"outlined"} onClick={handleOnClear} style={{ marginTop: 10 }}>
                        Clear Report
                    </Button>
                </Grid2>
            </Grid2>
            <Grid2 style={{ marginTop: 5 }}>
                <ReportAdvTable />
            </Grid2>
        </Grid2>
    );
    return (
        <Container maxWidth={"xl"} className={"atsGlobalContainer"}>
            <AddAdvertisingForm isForReport onRefresh={onRefreshForm} />
            <StatusHandler status={loadingStatus} showLoading />
            <StatusHandler status={sendingEmail} showLoading loadingMessage={"Sending..."} />
            <Grid2 container columnSpacing={6} justify="flex-start" alignItems="flex-start">
                {leftSide}
                {rightSide}
            </Grid2>
            <PdfViewer title={previewTitle} canSend onSend={handleReportSend} />

            {openEmailDialog && (
                <EmailDialog
                    ccList={ccList}
                    subject={subject}
                    onSend={handleOnSendEmail}
                    onClose={handleEmailDialogOnClose}
                    primaryList={[primaryUser]}
                />
            )}

            {contactAnchor && (
                <Popover
                    open={true}
                    anchorEl={contactAnchor}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                >
                    <SimpleContact
                        onAdd={handleAddSimpleContact}
                        onSave={handleSaveSimpleContact}
                        defaultContact={customContact}
                        onClose={handleCloseSimpleContact}
                    />
                </Popover>
            )}
        </Container>
    );
}

AdvReport.propTypes = {
    filmRelease: PropTypes.object,
    onLoadReportView: PropTypes.func,
    onClear: PropTypes.func,
    onPreviewPdf: PropTypes.func,
    loadingStatus: PropTypes.string,
    ccList: PropTypes.array,
    advertisementTypes: PropTypes.array,
    onSetPrimaryUser: PropTypes.func,
    onSetCCList: PropTypes.func,
    onSendEmail: PropTypes.func,
    sendingEmail: PropTypes.string,
    onResetEmailState: PropTypes.func,
    saveContact: PropTypes.func,
    getCompanyAndContacts: PropTypes.func,
    contactState: PropTypes.object,
    primaryUser: PropTypes.object,
};

export default AdvReport;
